/**
 * Generic Pagination Types
 *
 * Reusable pagination interfaces for API responses and frontend data handling.
 * Follows BiteWise naming conventions: camelCase for TypeScript, snake_case for API.
 *
 * @module types/pagination
 */

/**
 * Generic pagination metadata (camelCase for frontend use)
 */
export interface PaginationMeta {
  /** Current page number (1-based) */
  page: number;
  /** Number of items per page */
  perPage: number;
  /** Total number of items across all pages */
  total: number;
  /** Total number of pages */
  totalPages: number;
}

/**
 * Generic pagination parameters for API requests
 */
export interface PaginationParams {
  /** Page number to fetch (1-based) */
  page?: number;
  /** Number of items per page */
  perPage?: number;
  /** Search term for filtering */
  search?: string;
  /** Sort field */
  sortBy?: string;
  /** Sort order */
  sortOrder?: 'asc' | 'desc';
}

/**
 * Generic paginated API response structure (snake_case from backend)
 */
export interface PaginatedApiResponse<T> {
  /** Array of data items */
  data: T[];
  /** Current page number */
  page: number;
  /** Number of items per page */
  per_page: number;
  /** Total number of items */
  total: number;
  /** Total number of pages */
  total_pages: number;
}

/**
 * Generic paginated response structure (camelCase for frontend)
 */
export interface PaginatedResponse<T> {
  /** Array of transformed data items */
  data: T[];
  /** Pagination metadata */
  pagination: PaginationMeta;
}

/**
 * Pagination state for UI components
 */
export interface PaginationState {
  /** Current page number */
  currentPage: number;
  /** Items per page */
  pageSize: number;
  /** Total number of items */
  totalItems: number;
  /** Total number of pages */
  totalPages: number;
  /** Whether there is a next page */
  hasNextPage: boolean;
  /** Whether there is a previous page */
  hasPreviousPage: boolean;
}

/**
 * Pagination actions for state management
 */
export interface PaginationActions {
  /** Go to specific page */
  goToPage: (page: number) => void;
  /** Go to next page */
  nextPage: () => void;
  /** Go to previous page */
  previousPage: () => void;
  /** Change page size */
  changePageSize: (pageSize: number) => void;
  /** Reset to first page */
  reset: () => void;
}

/**
 * Complete pagination hook return type
 */
export interface UsePaginationReturn {
  /** Current pagination state */
  state: PaginationState;
  /** Pagination actions */
  actions: PaginationActions;
}

/**
 * Default pagination configuration
 */
export const DEFAULT_PAGINATION = {
  /** Default page size */
  PAGE_SIZE: 20,
  /** Default page number */
  PAGE: 1,
  /** Maximum page size allowed */
  MAX_PAGE_SIZE: 100,
  /** Minimum page size allowed */
  MIN_PAGE_SIZE: 5,
} as const;

/**
 * Pagination utility functions
 */
export const PaginationUtils = {
  /**
   * Calculate total pages from total items and page size
   */
  calculateTotalPages(total: number, pageSize: number): number {
    return Math.ceil(total / pageSize);
  },

  /**
   * Check if page number is valid
   */
  isValidPage(page: number, totalPages: number): boolean {
    return page >= 1 && page <= totalPages;
  },

  /**
   * Get page range for pagination display
   */
  getPageRange(currentPage: number, totalPages: number, maxVisible: number = 5): number[] {
    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, currentPage - half);
    const end = Math.min(totalPages, start + maxVisible - 1);

    // Adjust start if we're near the end
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  },

  /**
   * Create pagination state from API response
   */
  createPaginationState(
    page: number,
    perPage: number,
    total: number,
    totalPages: number
  ): PaginationState {
    return {
      currentPage: page,
      pageSize: perPage,
      totalItems: total,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    };
  },
};